import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, tap } from 'rxjs';
import { environment } from '../../environments/environment';

export interface AgencyUser {
  id: number;
  email: string;
  firstName?: string;
  lastName?: string;
  title?: string;
  phoneNumber?: string;
  fonction?: string;
  image?: string;
  kind: string;
  role: string;
  roleLabel: string;
  status: 'PENDING' | 'ACTIVE' | 'DISABLED';
  statusLabel: string;
  mainAccount: boolean;
  self: boolean;
  invitationExpired: boolean;
  invitedAt?: string;
  activatedAt?: string;
  lastLoginAt?: string;
  permissions: string[];
  /** Lot G (G4) : état on/off de chaque droit pour les toggles admin. */
  permissionState?: Record<string, boolean>;
  /** Lot G (Q6) : toggles spécifiques à ce compte (sinon = défauts du rôle). */
  customPermissions?: boolean;
  /** Lot G : rôle modifiable (un des 5 rôles collaborateurs). */
  collaboratorRole?: boolean;
}

/** Lot G (G3) : compte professionnel créé via l'app mobile, en attente de validation. */
export interface PendingProAccount {
  id: number;
  email: string;
  firstName?: string;
  lastName?: string;
  kind?: string;
  phoneNumber?: string;
  createdAt?: string;
  status: string;
  /** Profil pro (comptes de santé) : fonction (Médecin / Infirmier(ère)), poste et statut pro choisis à l'inscription. */
  candidatId?: number;
  fonction?: string;
  poste?: string;
  proStatus?: 'VACATAIRE' | 'TITULAIRE' | 'REMPLACANT';
  proStatusLabel?: string;
  /** Statuts autorisés pour la fonction (médecin : REMPLACANT ; infirmier : VACATAIRE / TITULAIRE). */
  allowedProStatuses?: string[];
  typeSecteurId?: number;
}

export interface AgencyUserPayload {
  email?: string;
  firstName?: string;
  lastName?: string;
  title?: string;
  phoneNumber?: string;
  fonction?: string;
  role?: string;
  image?: string;
}

/** "Gestion des collaborateurs" API (admin only — enforced by the backend). */
@Injectable({ providedIn: 'root' })
export class AgencyUsersService {
  private base = `${environment.api.BASE_URL}/agencies/users`;
  private refreshSubject = new Subject<void>();
  readonly refresh$ = this.refreshSubject.asObservable();
  /** Nombre de comptes pro en attente (badge du menu « Comptes professionnels »). */
  private pendingCountSubject = new BehaviorSubject<number>(0);
  readonly pendingProCount$ = this.pendingCountSubject.asObservable();

  constructor(private http: HttpClient) {}

  list(): Observable<AgencyUser[]> {
    return this.http.get<AgencyUser[]>(this.base);
  }

  invite(payload: AgencyUserPayload): Observable<AgencyUser> {
    return this.http.post<AgencyUser>(`${this.base}/invite`, payload);
  }

  update(id: number, payload: AgencyUserPayload): Observable<AgencyUser> {
    return this.http.put<AgencyUser>(`${this.base}/${id}`, payload);
  }

  changeRole(id: number, role: string): Observable<AgencyUser> {
    return this.http.put<AgencyUser>(`${this.base}/${id}/role`, { role });
  }

  changeStatus(id: number, status: 'ACTIVE' | 'DISABLED'): Observable<AgencyUser> {
    return this.http.put<AgencyUser>(`${this.base}/${id}/status`, { status });
  }

  resendInvitation(id: number): Observable<AgencyUser> {
    return this.http.post<AgencyUser>(`${this.base}/${id}/resend-invitation`, {});
  }

  /** Lot G (G4) : lit l'état des toggles d'un compte (défauts du rôle + overrides). */
  getPermissions(id: number): Observable<{ permissions: Record<string, boolean>; defaults: string[] }> {
    return this.http.get<{ permissions: Record<string, boolean>; defaults: string[] }>(`${this.base}/${id}/permissions`);
  }

  /** Lot G (G4) : applique les toggles pour CE compte seul (jamais le rôle). */
  updatePermissions(id: number, permissions: Record<string, boolean>): Observable<{ permissions: Record<string, boolean> }> {
    return this.http.put<{ permissions: Record<string, boolean> }>(`${this.base}/${id}/permissions`, { permissions });
  }

  /** Lot G (G3) : comptes pro en attente de validation. */
  listPendingProAccounts(): Observable<{ items: PendingProAccount[]; total: number }> {
    return this.http.get<{ items: PendingProAccount[]; total: number }>(`${environment.api.BASE_URL}/agencies/pro-accounts/pending`);
  }

  /** Valide (active) un compte pro en attente ; `proStatus` (optionnel) corrige le statut du pro. */
  validateProAccount(id: number, proStatus?: string): Observable<PendingProAccount> {
    return this.http.post<PendingProAccount>(`${environment.api.BASE_URL}/agencies/pro-accounts/${id}/validate`,
      proStatus ? { proStatus } : {});
  }

  /** Refuse un compte pro en attente ; le motif (optionnel) est transmis au pro par e-mail. */
  rejectProAccount(id: number, reason?: string): Observable<any> {
    return this.http.post(`${environment.api.BASE_URL}/agencies/pro-accounts/${id}/reject`,
      reason ? { reason } : {});
  }

  /** Recharge le compteur du badge (silencieux : un échec laisse la valeur précédente). */
  refreshPendingProCount(): void {
    this.http.get<{ count: number }>(`${environment.api.BASE_URL}/agencies/pro-accounts/pending/count`).subscribe({
      next: (res) => this.pendingCountSubject.next(res?.count ?? 0),
      error: () => {},
    });
  }

  /** Aligne le badge sur une liste déjà chargée (évite un appel de plus). */
  setPendingProCount(count: number): void {
    this.pendingCountSubject.next(count);
  }

  remove(id: number): Observable<any> {
    return this.http.delete(`${this.base}/${id}`);
  }

  /** Asks the collaborator list to reload (after an action in a cell / dialog). */
  refresh(): void {
    this.refreshSubject.next();
  }

  /** Human message of a failed call (the backend sends French business messages). */
  static errorMessage(err: any, fallback = 'Une erreur est survenue.'): string {
    return err?.error?.message || fallback;
  }
}
